import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import CallButton from "../components/CallButton";
import { api } from "../lib/api";
import { Anchor, Plane, Box, FileText, MapPin, Clock, Loader2 } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const StatusPill = ({ status, method, tStatus }) => {
  const cls =
    status === "Delivered" ? "status-Delivered"
      : status === "In Transit" ? "status-InTransit"
      : status === "Ready for Pickup" || status === "At Port" ? "status-AtPort"
      : status === "At Customs" ? "status-AtCustoms"
      : "status-Commissioned";
  return (
    <span className={`${cls} inline-block px-3 py-1 text-[10px] tracking-[0.25em] uppercase rounded-sm`}>
      {tStatus(status, method)}
    </span>
  );
};

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function Track() {
  const { id } = useParams();
  const { t, tStatus } = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/shipments/track/${id}`);
        if (active) setData(res.data);
      } catch (e) {
        if (active) setError(t("ledger.error_unknown"));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id, t]);

  return (
    <div data-testid="track-page" className="min-h-screen flex flex-col" style={{ background: "#1B3022" }}>
      <Navbar />
      <main className="flex-1 max-w-4xl w-full mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3 ornament">
          {t("ledger.eyebrow")}
        </div>
        <h1 className="font-serif-bv text-4xl sm:text-5xl lg:text-6xl text-[#F5EFE0]">
          {t("ledger.manifest")} <span className="italic text-[#D4AF37]">{id}</span>
        </h1>

        {loading && (
          <div className="mt-12 flex items-center gap-3 text-[#F5EFE0]/70">
            <Loader2 className="animate-spin" size={18} /> {t("common.loading")}
          </div>
        )}

        {error && !loading && (
          <div data-testid="track-error" className="mt-12 p-8 border border-[#C8102E]/50 bg-[#0F1D14]/80 rounded-sm">
            <p className="font-serif-bv italic text-2xl text-[#E5A07A]">{error}</p>
            <Link to="/" className="btn-outline-gold rounded-sm px-6 py-3 text-xs mt-6 inline-block">{t("commission.return_home")}</Link>
          </div>
        )}

        {data && !loading && (
          <div data-testid="track-result" className="mt-10 fade-up">
            <div className="border border-[#D4AF37]/40 bg-[#0F1D14]/80 rounded-sm overflow-hidden">
              <div className="border-b border-[#D4AF37]/30 bg-[#0F1D14] px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/70">{t("commission.tracking_label")}</div>
                  <div className="font-serif-bv text-2xl text-[#D4AF37] tabular">{data.shipment.tracking_id}</div>
                </div>
                <StatusPill status={data.shipment.current_status} method={data.shipment.method} tStatus={tStatus} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-6 py-5">
                <Info label={t("ledger.sender")} value={data.shipment.sender_name} />
                <Info label={t("ledger.receiver")} value={data.shipment.receiver_name} />
                <Info label={t("ledger.cargo")} value={data.shipment.cargo_type} icon={data.shipment.cargo_type === "Documents" ? FileText : Box} />
                <Info label={t("ledger.method")} value={data.shipment.method} icon={data.shipment.method === "Air Freight" ? Plane : Anchor} />
              </div>
              <div className="px-6 py-4 flex items-center gap-3 text-sm text-[#F5EFE0]/85 border-t border-[#D4AF37]/20">
                <span className="font-serif-bv text-lg text-[#D4AF37]">{data.shipment.origin_city}</span>
                <span className="flex-1 mx-2 h-px bg-gradient-to-r from-[#D4AF37] via-[#D4AF37]/40 to-[#D4AF37]" />
                <span className="font-serif-bv text-lg text-[#D4AF37]">{data.shipment.destination_city}</span>
              </div>
            </div>

            <div className="mt-10">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-5 ornament text-center">{t("ledger.journey")}</div>
              <ol className="relative border-l border-[#D4AF37]/40 ml-3 space-y-7">
                {data.milestones.length === 0 && (
                  <li className="pl-6 text-[#F5EFE0]/70 text-sm">{t("ledger.no_milestones")}</li>
                )}
                {data.milestones.slice().reverse().map((m, idx) => (
                  <li key={m.update_id} className="pl-8 relative">
                    <span className={`absolute -left-[9px] top-1 h-4 w-4 rounded-full ${idx === 0 ? "bg-[#D4AF37] ring-4 ring-[#D4AF37]/25" : "bg-[#0F1D14] border-2 border-[#D4AF37]"}`} />
                    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4">
                      <div className="flex items-center gap-2 text-[#D4AF37] font-serif-bv text-lg">
                        <MapPin size={14} /> {m.location}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-[#F5EFE0]/60">
                        <Clock size={12} /> {formatTime(m.timestamp)}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-[#F5EFE0]/85">{m.notes}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </main>
      <Footer />
      <CallButton testid="track-call-fab" />
      <WhatsAppButton variant="floating" trackingId={id} testid="track-whatsapp" />
    </div>
  );
}

const Info = ({ label, value, icon: Icon }) => (
  <div>
    <div className="text-[10px] tracking-[0.25em] uppercase text-[#F5EFE0]/50">{label}</div>
    <div className="text-sm text-[#F5EFE0] mt-1 flex items-center gap-2">
      {Icon ? <Icon size={14} className="text-[#D4AF37]" /> : null}
      {value}
    </div>
  </div>
);
